// Camera points files, unit detection and level grouping.
import * as THREE from 'three';

export interface CameraPoint {
  name: string;
  /** Raw position as written in the file (file units, file axes). */
  position: [number, number, number];
  /** Heading in degrees, or null when the file has none. */
  yaw: number | null;
}

export interface CameraFile {
  /** True for 3ds Max style Z-up coordinates. */
  zUp: boolean;
  /** 'm' when the file states metres; null means "same units as the model". */
  units: 'm' | null;
  /** True when every camera has a heading. */
  hasYaw: boolean;
  /** Metres per unit of the model exported with these points (the ArchviewVR Toolkit records it), when known. */
  modelScale?: number;
  cameras: CameraPoint[];
}

/**
 * Reads a camera file: either ArchviewVR JSON ({ zUp, units, panos: [{ name, position, yaw }] })
 * or plain text with one `name x y z [yaw]` per line (3ds Max coordinates, Z up, model units).
 */
export function parseCameraFile(text: string): CameraFile {
  const t = text.trim();
  if (t.startsWith('{') || t.startsWith('[')) {
    const j = JSON.parse(t);
    const list: any[] = Array.isArray(j) ? j : j.panos || j.cameras || [];
    const cameras = list
      .filter((c) => c && c.name != null && Array.isArray(c.position))
      .map((c) => ({ name: String(c.name), position: c.position.map(Number) as [number, number, number], yaw: Number(c.yaw) || 0 }));
    const modelScale = Number(j.modelScale);
    return { zUp: !!j.zUp, units: j.units === 'm' ? 'm' : null, hasYaw: true, cameras, ...(modelScale > 0 ? { modelScale } : {}) };
  }
  const cameras: CameraPoint[] = [];
  for (const line of t.split(/\r?\n/)) {
    const m = line.trim().split(/[\s,;]+/);
    if (m.length < 4) continue;
    const nums = m.slice(1, 5).map(Number);
    if (!nums.slice(0, 3).every(Number.isFinite)) continue;
    cameras.push({
      name: m[0],
      position: [nums[0], nums[1], nums[2]],
      yaw: m.length >= 5 && Number.isFinite(nums[3]) ? nums[3] : null,
    });
  }
  if (!cameras.length) throw new Error('No camera points found');
  return { zUp: true, units: null, hasYaw: cameras.every((c) => c.yaw != null), cameras };
}

/** Converts a camera point into three.js metres (Y up). */
export function cameraToWorld(cam: CameraPoint, file: CameraFile, modelScale: number): THREE.Vector3 {
  const s = file.units === 'm' ? 1 : modelScale;
  const [x, y, z] = cam.position.map((v) => v * s);
  return file.zUp ? new THREE.Vector3(x, z, -y) : new THREE.Vector3(x, y, z);
}

/**
 * Finds the camera for a pano file name. Exact match first, then the longest camera name the
 * file name contains (or that contains it).
 */
export function matchCamera(fileBase: string, file: CameraFile): CameraPoint | null {
  const key = fileBase.toLowerCase();
  const exact = file.cameras.find((c) => c.name.toLowerCase() === key);
  if (exact) return exact;
  let best: CameraPoint | null = null;
  for (const c of file.cameras) {
    const ck = c.name.toLowerCase();
    if (key.includes(ck) || ck.includes(key)) {
      if (!best || ck.length > best.name.length) best = c;
    }
  }
  return best;
}

const UNIT_CANDIDATES: Array<[number, string]> = [
  [1, 'metres'],
  [0.001, 'millimetres'],
  [0.01, 'centimetres'],
  [0.0254, 'inches'],
  [1000, 'kilometres'],
];

/** True when a building of this raw size makes sense at this scale (2 to 600 m across). */
export function plausibleScale(size: THREE.Vector3, scale: number): boolean {
  const m = Math.max(size.x, size.z) * scale;
  return m >= 2 && m <= 600;
}

/** True when the model makes sense as a building at this scale: 2 to 600 m across and 1.5 to 300 m tall. */
function buildingFits(size: THREE.Vector3, scale: number): boolean {
  const w = Math.max(size.x, size.z) * scale, h = size.y * scale;
  return w >= 2 && w <= 600 && h >= 1.5 && h <= 300;
}

/**
 * Picks model units from the raw bounding-box size. Metres when that makes sense; otherwise the units
 * that give a building closest to a typical size (about 12 m across), so a 2,800-unit-wide model reads
 * as centimetres (28 m) rather than millimetres (2.8 m). The cameras check this again after upload.
 */
export function detectUnits(rawSize: THREE.Vector3): { scale: number; name: string } {
  if (buildingFits(rawSize, 1)) return { scale: 1, name: 'metres' };
  let best: { s: number; n: string; d: number } | null = null;
  for (const [s, n] of UNIT_CANDIDATES) {
    if (s === 1 || !buildingFits(rawSize, s)) continue;
    const d = Math.abs(Math.log((Math.max(rawSize.x, rawSize.z) * s) / 12));
    if (!best || d < best.d) best = { s, n, d };
  }
  if (best) return { scale: best.s, name: best.n };
  const hit = UNIT_CANDIDATES.find(([s]) => plausibleScale(rawSize, s)) || UNIT_CANDIDATES[0];
  return { scale: hit[0], name: hit[1] };
}

/**
 * Groups eye heights into levels. Heights within 0.35 m of the next make a group; a group is a level
 * when it has enough cameras (3, or 2 on a small tour of under 15 renders). Lone cameras between levels
 * (stairs, landings) aren't levels. Each level sits at its group's middle height.
 */
export function computeLevels(heights: number[]): number[] {
  const ys = [...heights].sort((a, b) => a - b);
  const groups: number[][] = [];
  for (const y of ys) {
    const g = groups[groups.length - 1];
    if (g && y - g[g.length - 1] < 0.35) g.push(y);
    else groups.push([y]);
  }
  const need = ys.length < 15 ? 2 : 3;
  const levels = groups.filter((g) => g.length >= need).map((g) => g[Math.floor(g.length / 2)]);
  if (levels.length) return levels;
  // nothing big enough: the busiest group
  const best = groups.reduce((m, g) => (g.length > m.length ? g : m), [] as number[]);
  return [best.length ? best[Math.floor(best.length / 2)] : 1.6];
}

/** The level each height belongs to: the closest one within 1 m, or null (between levels). */
export function assignLevels(levels: number[], heights: number[]): Array<number | null> {
  return heights.map((y) => {
    let bi = -1, bd = 1.0;
    levels.forEach((l, i) => { const d = Math.abs(l - y); if (d < bd) { bd = d; bi = i; } });
    return bi < 0 ? null : bi;
  });
}

/** Index of the level nearest a height. */
export function levelIndexFor(levels: number[], y: number): number {
  let bi = 0;
  levels.forEach((l, i) => {
    if (Math.abs(l - y) < Math.abs(levels[bi] - y)) bi = i;
  });
  return bi;
}

/** Default level names: Ground, Level 1, Level 2... */
export function levelName(i: number): string {
  return i === 0 ? 'Ground' : `Level ${i}`;
}

/** 3ds Max script that exports camera positions and headings as ArchviewVR JSON. */
export const MAXSCRIPT_EXPORT = `-- ArchviewVR camera export (3ds Max)
fn avrUnit = (
  local t = case units.SystemType of (
    #millimeters: 0.001; #centimeters: 0.01; #meters: 1.0
    #kilometers: 1000.0; #inches: 0.0254; #feet: 0.3048; default: 1.0)
  t * units.SystemScale
)
s = avrUnit()
cams = for c in cameras where superclassof c == camera collect c
str = "{\\"format\\":\\"archviewvr/1\\",\\"zUp\\":true,\\"units\\":\\"m\\",\\"panos\\":["
for i = 1 to cams.count do (
  c = cams[i]
  f = -c.transform.row3
  yaw = atan2 f.x f.y
  p = c.pos * s
  str += "{\\"name\\":\\"" + c.name + "\\",\\"position\\":[" + (p.x as string) + "," + (p.y as string) + "," + (p.z as string) + "],\\"yaw\\":" + (yaw as string) + "}"
  if i < cams.count do str += ","
)
str += "]}"
setclipboardText str
format "ArchviewVR: % cameras copied to clipboard\\n" cams.count`;
